/**
 * 다이얼로그 크기 사용자 prefs (localStorage)
 * key: erp.dialog.size|{prefsUserId}|{sizeKey}
 * value: { width: number, height: number }
 *
 * prefsUserId는 buildPrefsUserId(tenant, user) = 기관|사용자
 */

const PREFIX = 'erp.dialog.size';

const MIN_W = 480;
const MIN_H = 360;

function storageKey(prefsUserId, sizeKey) {
  const uid = String(prefsUserId || '').trim() || 'anon';
  const sk = String(sizeKey || '').trim() || 'default';
  return [PREFIX, uid, sk].join('|');
}

function parsePx(v, fallback) {
  if (typeof v === 'number' && Number.isFinite(v)) return Math.round(v);
  const n = Number(String(v || '').replace(/px$/i, '').trim());
  return Number.isFinite(n) && n > 0 ? Math.round(n) : fallback;
}

export function clampDialogSize(width, height, { minW = MIN_W, minH = MIN_H } = {}) {
  const maxW = Math.max(minW, window.innerWidth - 24);
  const maxH = Math.max(minH, window.innerHeight - 24);
  return {
    width: Math.min(maxW, Math.max(minW, Math.round(width))),
    height: Math.min(maxH, Math.max(minH, Math.round(height))),
  };
}

/**
 * @returns {{ width: number, height: number } | null}
 */
export function loadDialogSize(prefsUserId, sizeKey) {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(prefsUserId, sizeKey));
    if (!raw) return null;
    const data = JSON.parse(raw);
    const w = Number(data?.width);
    const h = Number(data?.height);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return clampDialogSize(w, h);
  } catch {
    return null;
  }
}

export function saveDialogSize(prefsUserId, sizeKey, width, height) {
  if (typeof localStorage === 'undefined') return;
  if (!sizeKey) return;
  try {
    const next = clampDialogSize(width, height);
    localStorage.setItem(
      storageKey(prefsUserId, sizeKey),
      JSON.stringify({ width: next.width, height: next.height, savedAt: new Date().toISOString() })
    );
  } catch {
    /* quota */
  }
}

/** props 기본값(px 문자열) + 저장본 → 초기 크기 */
export function resolveDialogSize({
  prefsUserId,
  sizeKey,
  dialogWidth = '860px',
  dialogHeight = '640px',
} = {}) {
  const fallback = {
    width: parsePx(dialogWidth, 860),
    height: parsePx(dialogHeight, 640),
  };
  if (!sizeKey) return clampDialogSize(fallback.width, fallback.height);
  const saved = loadDialogSize(prefsUserId, sizeKey);
  if (saved) return saved;
  return clampDialogSize(fallback.width, fallback.height);
}

export const DIALOG_SIZE_MIN = { width: MIN_W, height: MIN_H };
